import React, { useState } from "react";
import PropTypes from "prop-types";
import Tippy from "@tippyjs/react";
import { Icon } from "../../Icon";
import { SlideOutMenuPanel } from "../SlideOutMenuPanel/SlideOutMenuPanel";
import styles from "./Okta.module.scss";

const defaultLabels = {
	openMenu: "Open account profile menu",
	closeMenu: "Close account profile menu",
	toggleDropdown: "Toggle account profile dropdown menu",
	mobileMenuTitle: "Account Profile",
	welcome: "Welcome",
};

const Okta = ({ labels: providedLabels, profileData, focusOnShouldIgnore, children }) => {
	const labels = { ...defaultLabels, ...providedLabels };

	const [isOktaSideMenuOpen, setOktaSideMenuToOpen] = useState(false);
	const [ariaExpanded, setAriaExpanded] = useState(false);

	const handleOktaSideMenuToggle = () => {
		setOktaSideMenuToOpen(!isOktaSideMenuOpen);
	};

	return (
		<div id="okta-menu">
			{/* Mobile Toggle */}
			<button
				type="button"
				role="menuitem"
				aria-label={labels.openMenu}
				aria-haspopup="true"
				id="accountSignin-side"
				className={styles.triggerSideMenu}
				aria-expanded={isOktaSideMenuOpen}
				onClick={handleOktaSideMenuToggle}>
				<Icon name="my-account--o" className={styles.accountIcon} />
			</button>
			<SlideOutMenuPanel
				id="account-slide-out-menu"
				classNames={{
					menu: styles.menu,
				}}
				labels={{ title: labels.mobileMenuTitle, closeMenu: labels.closeMenu }}
				isOpen={isOktaSideMenuOpen}
				handleClose={handleOktaSideMenuToggle}
				focusOnShouldIgnore={focusOnShouldIgnore}>
				{children}
			</SlideOutMenuPanel>
			{/* Tablet + Desktop Toggle */}
			<Tippy
				role="menuitem"
				placement="bottom-end"
				trigger="click"
				interactive={true}
				content={
					<div id="account-menu" className={styles.dropdownMenu}>
						{children}
					</div>
				}
				appendTo="parent"
				plugins={[
					{
						name: "hideOnPopperBlurAndEsc",
						defaultValue: true,
						fn(instance) {
							function onKeyDown(event) {
								if (event.code === "Escape") {
									instance.hide();
								}
							}

							return {
								onCreate() {
									instance.popper.addEventListener("focusout", event => {
										if (
											instance.props.hideOnPopperBlurAndEsc &&
											event.relatedTarget &&
											!instance.popper.contains(event.relatedTarget)
										) {
											instance.hide();
										}
									});
								},
								onShow() {
									document.addEventListener("keydown", onKeyDown);
								},
								onHide() {
									document.removeEventListener("keydown", onKeyDown);
								},
							};
						},
					},
				]}
				onShow={() => setAriaExpanded("true")}
				onHide={() => setAriaExpanded("false")}>
				<button
					type="button"
					role="menuitem"
					aria-label={labels.toggleDropdown}
					aria-haspopup="true"
					aria-controls="account-menu"
					id="accountSignin"
					className={styles.triggerDDMenu}
					aria-expanded={ariaExpanded}>
					<Icon name="my-account--o" className={styles.accountIcon} />
					{profileData?.isUserLoggedIn && (
						<span className={styles.profileText}>
							<span className={styles.greeting}>{labels.welcome}</span>
							{profileData?.userName && (
								<span className={styles.userName}>{profileData?.userName}</span>
							)}
							<Icon className={styles.icon} name="arrow-down" aria-hidden="true" />
						</span>
					)}
				</button>
			</Tippy>
		</div>
	);
};

Okta.propTypes = {
	labels: PropTypes.shape({
		openMenu: PropTypes.string,
		closeMenu: PropTypes.string,
		toggleDropdown: PropTypes.string,
		mobileMenuTitle: PropTypes.string,
		welcome: PropTypes.string,
	}),
	profileData: PropTypes.shape({
		isUserLoggedIn: PropTypes.bool,
		userName: PropTypes.string,
	}),
	focusOnShouldIgnore: PropTypes.func,
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

Okta.defaultProps = {
	labels: defaultLabels,
	profileData: {
		isUserLoggedIn: false,
		userName: undefined,
	},
	focusOnShouldIgnore: () => true,
};

export default Okta;
export { Okta };
